import React, { useState, useEffect, useCallback } from 'react';
import Login from './Login';
import LocationManagement from './LocationManagement.js';  // Import the component
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

function App() {
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [bins, setBins] = useState([]);
    const [selectedBin, setSelectedBin] = useState(null);
    const [items, setItems] = useState([]);
    const [newClientName, setNewClientName] = useState('');
    const [newBinName, setNewBinName] = useState('');
    const [newItemName, setNewItemName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedLocationForBin, setSelectedLocationForBin] = useState(null); 
    const [token, setToken] = useState(null);

    const handleLogin = (token) => {
        setToken(token);
    };

    // Fetch all clients when the component loads
    useEffect(() => {
        console.log('*** APP useEffect: (on load)');

        if (token) {
            fetch('/api/clients', {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(response => response.json())
                .then(data => setClients(data))
                .catch(error => console.error('Error fetching clients:', error));
        }
    }, [token]);

    const refreshBinsAndLocations = useCallback((clientId) => {
        console.log("+++ APP const function: refreshBinsAndLocations for client ID:", clientId);

        if (token) {
            // Re-fetch bins
            fetch(`/api/bin/${clientId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(response => response.json())
                .then(data => setBins(data))
                .catch(error => console.error('Error fetching bins:', error));

            // Re-fetch locations
            fetch(`/api/locations/client/${clientId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(res => res.json())
                .then(data => setLocations(data))
                .catch(console.error);
        }
    }, [token]);

    // Fetch bins & locations when the selected client changes
    useEffect(() => {
        console.log('*** APP useEffect: selectedClient - fetch bins and location');

        if (selectedClient) {
            refreshBinsAndLocations(selectedClient);
        }
    }, [selectedClient, refreshBinsAndLocations]);

    // Fetch items when the selected bin changes
    useEffect(() => {
        console.log('*** APP useEffect: selectedBin && selectedClient');

        if (selectedBin && selectedClient) {
            console.log("Fetching items for bin:", selectedBin, "and client:", selectedClient);  // Debugging line
            fetch(`/api/items/${selectedClient}/${selectedBin}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(response => response.json())
                .then(data => {
                    if (Array.isArray(data)) {
                        setItems(data);
                    } else {
                        setItems([]);
                        console.error('Expected array but received:', data);
                    }
                })
                .catch(error => {
                    console.error('Error fetching items:', error);
                    setItems([]);
                });
        } else {
            setItems([]);
        }
    }, [selectedBin, selectedClient, token]);

    // Function to connect a bin to a location
    const handleConnectBinToLocation = () => {
        console.log('+++ APP const function: handleConnectBinToLocation');

        if (!selectedBin || !selectedLocationForBin) {
            alert("Please select both a bin and a location.");
            return;
        }

        fetch(`/api/bin/${selectedBin}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ location_id: selectedLocationForBin }),
        })
            .then((res) => res.json())
            .then((updatedBin) => {
                // Update the bins state with the updated bin
                setBins((prevBins) =>
                    prevBins.map((bin) =>
                        bin.id === updatedBin.id ? { ...bin, ...updatedBin } : bin
                    )
                );
                alert("Bin successfully connected to location!");

                // Re-fetch bins and locations to update the dropdown
                refreshBinsAndLocations(selectedClient);
            })
            .catch((err) => console.error("Error connecting bin to location:", err));
    };

    // Handle client selection
    const handleClientChange = (e) => {
        console.log('+++ APP const function: handleClientChange');

        setSelectedClient(e.target.value);
        setSelectedBin(null);  // Reset bin selection when client changes
        setSearchResults([]);   // Reset search results when client changes
    };

    const handleBinChange = (e) => {
        console.log('+++ APP const function: handleBinChange');

        const binId = e.target.value;
        console.log("Selected bin ID:", binId);  // Debugging line
        setSelectedBin(binId);
    };

    const handleAddClient = () => {
        console.log('+++ APP const function: handleAddClient');

        if (!newClientName.trim()) {
            console.error("Client name is empty");
            return;
        }
    
        console.log("Adding client:", newClientName);  // Debugging line
    
        fetch('/api/clients', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ client_name: newClientName }),
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Error adding client');
            }
            return response.json();
        })
        .then(newClient => {
            console.log("Client added successfully:", newClient);  // Debugging line
            setClients([...clients, newClient]);
            setNewClientName('');
            setSelectedClient(newClient.client_id);
        })
        .catch(error => {
            console.error('Error adding client:', error);
        });
    };
    

    const handleAddBin = () => {
        console.log('+++ APP const function: handleAddBin');

        if (!newBinName.trim()) {
            return alert("Bin name cannot be empty.");
        }
        fetch('/api/bin', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ bin_name: newBinName, client_id: selectedClient }),
        })
            .then(response => response.json())
            .then(newBin => {
                setBins([...bins, newBin]);
                setNewBinName('');
                setSelectedBin(newBin.id);
            })
            .catch(error => console.error('Error adding bin:', error));
    };

    // Delete an existing Bin
    const handleDeleteBin = (binId) => {
        console.log('+++ APP const function: handleDeleteBin');

        fetch(`/api/bin/${binId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to delete bin');
                }
                return response.json();
            })
            .then(() => {
                // Remove the bin from state
//                setBins((prevBins) => prevBins.filter((bin) => bin.id !== binId));
                refreshBinsAndLocations(selectedClient);  // Refresh bins and locations
                setSelectedBin(null); // Reset selectedBin
                alert('Bin successfully deleted!');
            })
            .catch((error) => {
                console.error('Error deleting bin:', error);
                alert('Failed to delete the bin. Please try again.');
            });
    };

    const handleAddItem = () => {
        console.log('+++ APP const function: handleAddItem');

        if (!newItemName.trim() || !selectedBin) {
            return alert("Item name and bin selection are required.");
        }
        fetch('/api/items', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ name: newItemName, bin_id: selectedBin, client_id: selectedClient }),
        })
            .then(response => response.json())
            .then(newItem => {
                setItems([...items, newItem]);
                setNewItemName('');
            })
            .catch(error => console.error('Error adding item:', error));
    };

    const handleDeleteItem = (itemId) => {
        console.log('+++ APP const function: handleDeleteItem');
    
        fetch(`/api/items/${itemId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to delete item');
                }
                return response.json();
            })
            .then(() => {
                // Remove the item from state
                setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
                alert('Item successfully deleted!');
            })
            .catch((error) => {
                console.error('Error deleting item:', error);
                alert('Failed to delete the item. Please try again.');
            });
    };

    
    // Handle search functionality
    const handleSearch = () => {
        console.log('+++ APP const function: handleSearch');

        if (!searchTerm.trim() || !selectedClient) {
            return alert("Search term and client selection are required.");
        }
        fetch(`/api/items/search?term=${encodeURIComponent(searchTerm)}&client_id=${selectedClient}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(response => response.json())
            .then(data => setSearchResults(data))
            .catch(error => {
                console.error('Error searching items:', error);
                setSearchResults([]);  // Clear search results on error
            });
    };

    const handleItemClick = (item) => {
        console.log('+++ APP const function: handleItemClick');

        setSelectedBin(item.bin_id);  // Set the selected bin based on the clicked item's bin
        setSearchTerm(''); // Optionally clear the search term
    };

    return (
        <div>
            {token ? (
                <div>
                    <h1>My Multi-Client Bin App</h1>

                    {/* Client Selection */}
                    <div>
                        <h2>Select or Add a Client</h2>
                        <select onChange={handleClientChange} value={selectedClient || ''}>
                            <option value="">-- Select a Client --</option>
                            {clients.map(client => (
                                <option key={client.client_id} value={client.client_id}>{client.client_name}</option>
                            ))}
                        </select>
                        <input
                            type="text"
                            placeholder="New Client Name"
                            value={newClientName}
                            onChange={(e) => setNewClientName(e.target.value)}
                        />
                        <button onClick={handleAddClient}>Add Client</button>
                    </div>

                    {/* Bin Selection */}
                    {selectedClient && (
                        <div>
                            <h2>Select or Add a Bin</h2>
                            <select onChange={handleBinChange} value={selectedBin || ''}>
                                <option value="">-- Select a Bin --</option>
                                {bins.map(bin => (
                                    <option key={bin.id} value={bin.id}>
                                        {bin.bin_name}   
                                        {/* Only display room and location if location <> null : */}
                                        {bin.location_name  
                                        ? `(${bin.room_name ? `${bin.room_name}, ` : ''}${bin.location_name})`
                                        : '(No Location)'}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="New Bin Name"
                                value={newBinName}
                                onChange={(e) => setNewBinName(e.target.value)}
                            />
                            <button onClick={handleAddBin}>Add Bin</button>

                            {/* Bin-to-Location Association */}
                            {selectedBin && (
                                <div>
                                    <h3>Connect Bin to a Location</h3>
                                    <select
                                        onChange={(e) => setSelectedLocationForBin(e.target.value)}
                                        value={selectedLocationForBin || ''}
                                    >
                                        <option value="">-- Select a Location --</option>
                                        {locations.map(location => (
                                            <option key={location.location_id} value={location.location_id}>
                                                {location.location_name} (Room: {location.room_name})
                                            </option>
                                        ))}
                                    </select>
                                    <button onClick={handleConnectBinToLocation}>Connect to Location</button>
                                </div>
                            )}

                        </div>
                        
                    )}

                    {/* Items List */}
                    {selectedBin && (
                        <div>
                            <h2>Selected bin: {bins.find(bin => bin.id === Number(selectedBin))?.bin_name}</h2>

                            {/* Display building, room, and location */}
                            <div>
                                {bins.find(bin => bin.id === Number(selectedBin))?.building_name && (
                                    <p>
                                        Location:{" "}
                                        {bins.find(bin => bin.id === Number(selectedBin))?.building_name} - 
                                        {bins.find(bin => bin.id === Number(selectedBin))?.room_name} - 
                                        {bins.find(bin => bin.id === Number(selectedBin))?.location_name}
                                    </p>
                                )}
                            </div>

                            <ul>
                                {Array.isArray(items) && items.map(item => (
                                    <li key={item.id}>
                                        {item.name}
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                            style={{ color: 'red', marginLeft: '10px', cursor: 'pointer' }}
                                            onClick={() => handleDeleteItem(item.id)}
                                        />
                                    </li>
                                ))}
                            </ul>

                            {/* Add New Item */}
                            <h3>Add New Item</h3>
                            <input
                                type="text"
                                placeholder="Item Name"
                                value={newItemName}
                                onChange={(e) => setNewItemName(e.target.value)}
                            />
                            <button onClick={handleAddItem}>Add Item</button>

                            {/* Delete Bin */}
                            <h3>Manage Bin</h3>
                            <button
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this bin? The items in the bin will also be erased...')) {
                                        handleDeleteBin(selectedBin);
                                    }
                                }}
                                style={{ color: 'red' }}
                            >
                                Delete Bin
                            </button>
                        </div>
                    )}

                    {/* Search Item section */}
                    {selectedClient && (
                        <div>
                            <h2>Search Items for Client</h2>
                            <input
                                type="text"
                                placeholder="Search for items..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <button onClick={handleSearch}>Search</button>

                            {/* Display Search Results */}
                            {searchResults.length > 0 && (
                                <div>
                                    <h3>Search Results:</h3>
                                    <table>
                                        <thead>
                                            <tr>
                                                {Object.keys(searchResults[0]).map((column) => (
                                                    <th key={column}>{column}</th>  // Display column headers
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map(item => (
                                                <tr key={item.id} onClick={() => handleItemClick(item)}>
                                                    {Object.values(item).map((value, index) =>(
                                                        <td key={index}>{value}</td>  // Display each column value 
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Location Management Section */}
                    {selectedClient && <LocationManagement clientId={selectedClient} token={token}/>}
                    
                    <div style={{ 
                        position: 'fixed', 
                        bottom: '10px', 
                        width: '100%', 
                        textAlign: 'center', 
                        backgroundColor: '#f0f0f0', 
                        padding: '10px',
                        borderTop: '1px solid #ccc'
                    }}>
                    </div>            
                </div>
            ) : (
                <Login onLogin={handleLogin} />
            )}
        </div>
    );
}

export default App;